/**
 * @file    useDrawerLifecycle.ts
 * @scope   SHARED
 * @summary Save / cancel / close lifecycle of the drawer: commits the edited widget to the store,
 *          strips filters where business rules require it, and tears the drawer down.
 * @consumes Global store writer, the enterprise persistence callback, the shared save-guard
 * @produces `handleSaveClick`, `handleFieldsManagerSaveClick`, `fieldsManagerSaveDisabled`,
 *           `handleCloseClick`, `handleCancelClick`, `isDisabled`
 * @sideEffects Publishes widget mutations, removes orphan widgets and toggles drawer/enterprise
 *              flags on the global store.
 * @notes These are event handlers (not effects): they only run on user interaction, so extracting
 *        them here carries no hook-ordering risk. `isDisabled` is returned because the footer needs
 *        it for the Save button while close/cancel need it internally — single source of truth.
 */
import { FilterDateOption, Widget } from 'models/WidgetModel';
import { library } from 'components';
import { initialGlobalState } from 'store/initialGlobalState';
import { Trend } from 'core-ui/SelectTrendInput/SelectTrendInput';
import { isSaveDisabled } from '../utils/validation';

export interface UseDrawerLifecycleParams {
  /** Widget currently being edited. */
  customWidget: any;
  /** Global store writer. */
  setState: (partial: any) => void;
  /** All widgets on the dashboard (used to find the one being edited on cancel). */
  widgets: any[];
  /** Payload GUID seeded from the saved widget; persisted back on save. */
  payLoad: string;
  /** Trend selected in the drawer. */
  selectedChartTrend?: { trend: Trend };
  /** True when editing an Enterprise V2 widget. */
  isDiyWidgetEngine: boolean;
  /** Serialises the Enterprise data into the widget on save (injected to keep this decoupled). */
  applyEnterpriseDataToWidget: (widget: Widget, trend: any, collections: any) => void;
  /** Data collections offered to Enterprise partitions. */
  availableCollections: any;
  /** Id of an already-saved Enterprise widget (drives the close-vs-cancel branch). */
  savedEnterpriseWidgetId: any;
  /** Removes a widget from the dashboard by numeric id. */
  removeWidget: (id: number) => void;
  /** Returns the drawer to its main tab. */
  goToMainTab: () => void;
  /** Persists the dashboard (owned by the Grid feature). */
  saveComponent: () => void;
  /** Partition currently focused inside the Enterprise fields manager. */
  selectedPartition: any;
  /** Enterprise-only guard for the fields-manager Save button. */
  isFieldsManagerSaveDisabled: (partition: any) => boolean;
  /** Enterprise-only widget validity check (injected to avoid importing the DiyEnterprise domain). */
  isEnterpriseWidgetInvalid: (config: any) => boolean;
  /** True when the Medusa query is valid (or the widget has none). */
  queryIsValid: boolean;
  /** True when the text-block content is valid (or the widget is not a text block). */
  textContentIsValid: boolean;
  /** True when the display name is required but missing. */
  requiredDisplayName: boolean;
  /** Rules applicable to the current chart. */
  applicableRules: any[];
  /** True when the chart still needs questions/calculations. */
  requiredQuestionsCalculations: boolean;
}

export interface UseDrawerLifecycleResult {
  handleSaveClick: (event: any) => void;
  handleFieldsManagerSaveClick: (event: any) => void;
  fieldsManagerSaveDisabled: () => boolean;
  handleCloseClick: () => void;
  handleCancelClick: () => void;
  isDisabled: (customWidget: any) => boolean;
}

/**
 * Builds the drawer's lifecycle handlers. Every branch mirrors the original inline implementation
 * verbatim so observable behaviour is unchanged.
 */
export const useDrawerLifecycle = ({
  customWidget,
  setState,
  widgets,
  payLoad,
  selectedChartTrend,
  isDiyWidgetEngine,
  applyEnterpriseDataToWidget,
  availableCollections,
  savedEnterpriseWidgetId,
  removeWidget,
  goToMainTab,
  saveComponent,
  selectedPartition,
  isFieldsManagerSaveDisabled,
  isEnterpriseWidgetInvalid,
  queryIsValid,
  textContentIsValid,
  requiredDisplayName,
  applicableRules,
  requiredQuestionsCalculations,
}: UseDrawerLifecycleParams): UseDrawerLifecycleResult => {
  /**
   * Thin adapter over `isSaveDisabled`.
   * WHY: the enterprise validator is passed as a callback so the shared guard never imports
   * anything from the DiyEnterprise domain.
   */
  const isDisabled = (cw: any) =>
    isSaveDisabled(cw, {
      queryIsValid,
      textContentIsValid,
      isDiyWidgetEngine,
      isEnterpriseInvalid: () => isEnterpriseWidgetInvalid(cw.customWidgetConfig),
      requiredDisplayName,
      hasApplicableRules: applicableRules.length > 0,
      requiredQuestionsCalculations,
    });

  const saveChanges = () => {
    const editedWidget: Widget = customWidget;
    if (payLoad != '' && payLoad != undefined) {
      customWidget.payload.DataSetGuid = payLoad;
    }
    if (customWidget?.customWidgetConfig?.trend?.length && selectedChartTrend?.trend) {
      const trendDone = editedWidget?.customWidgetConfig?.trend
        ? editedWidget?.customWidgetConfig?.trend[0]
        : {};
      if (editedWidget.customWidgetConfig) {
        editedWidget.customWidgetConfig.trend = [trendDone] as any;
      }
    }

    // For DynamicTable/OneQuestionOneCalculation Date Filter should be null to avoid take MTD as default
    // Set filter as null when widget is TextBlock
    if (editedWidget.customWidgetConfig?.filters && customWidget.alias === 'TextBlock') {
      editedWidget.customWidgetConfig.filters = null;
    } else if (
      editedWidget.customWidgetConfig?.filters?.currentSelection &&
      customWidget.customWidgetConfig?.rules?.length &&
      customWidget.customWidgetConfig?.rules[0]?.ruleName === 'OneQuestionOneCalculation'
    ) {
      if (
        editedWidget.customWidgetConfig.filters &&
        editedWidget.customWidgetConfig.filters.currentSelection &&
        editedWidget.customWidgetConfig.filters.currentSelection.dateFilter
      ) {
        editedWidget.customWidgetConfig.filters.currentSelection!.dateFilter = null as
          | FilterDateOption
          | null
          | undefined;
      }
    }

    if (isDiyWidgetEngine) {
      applyEnterpriseDataToWidget(editedWidget, selectedChartTrend?.trend, availableCollections);
    }
    setState({
      selectedDiyChildChart: null,
      customWidget: editedWidget,
      ...(isDiyWidgetEngine ? { savedEnterpriseWidgetId: editedWidget.id } : {}),
    });
  };

  const handleSaveClick = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    saveChanges();

    setState({ diySave: true });
    if (!isDiyWidgetEngine) setState({ isDiyWidgetEngine: false });
    setState({ diyMode: false });
  };

  const handleFieldsManagerSaveClick = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    saveChanges();
    goToMainTab();
    saveComponent();
  };

  const fieldsManagerSaveDisabled = () => isFieldsManagerSaveDisabled(selectedPartition);

  const handleCloseClick = () => {
    if (savedEnterpriseWidgetId && !isDisabled(customWidget)) {
      setState({ diyMode: false });
    } else {
      handleCancelClick();
    }
  };

  const handleCancelClick = () => {
    const selectedWidget = widgets.find((widget) => widget.config.i === customWidget.config.i);

    if (
      selectedWidget?.alias === library.CUSTOM_WIDGET ||
      selectedWidget?.alias === library.QUERY_WIDGET ||
      (savedEnterpriseWidgetId && isDisabled(customWidget))
    ) {
      removeWidget(parseInt(customWidget.config.i));
    }

    setState({ customWidget: initialGlobalState.customWidget, selectedDiyChildChart: null });
    setState({ isDiyWidgetEngine: false });
    setState({ diyMode: false });
  };

  return {
    handleSaveClick,
    handleFieldsManagerSaveClick,
    fieldsManagerSaveDisabled,
    handleCloseClick,
    handleCancelClick,
    isDisabled,
  };
};
